import React, { useState, useEffect } from "react";
import { Row, Col, Button, Select, Typography, Grid } from "antd";
import {
  UserOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  DollarOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

// Helper component for the styled label
const FilterLabel = ({ label, isMobile }) => (
  <Text
    style={{
      fontSize: isMobile ? 9 : 10,
      fontWeight: 500,
      color: "#8c8c8c",
      textTransform: "uppercase",
      lineHeight: 1,
      textAlign: "left",
      display: "block",
    }}
  >
    {label}
  </Text>
);

// Helper component for the filter field structure
const FilterField = ({
  icon,
  label,
  children,
  isLast = false,
  flexValue = 3,
  isMobile = false,
}) => {
  const filterItemStyle = {
    borderRight: isLast ? "none" : "1px solid #e8e8e8",
    height: "100%",
    flex: flexValue,
    display: "flex",
    alignItems: "center",
    padding: isMobile ? "12px 10px" : "0 15px",
    borderBottom: isMobile && !isLast ? "1px solid #e8e8e8" : "none",
  };

  const iconStyle = {
    fontSize: isMobile ? 16 : 18,
    color: "#8c8c8c",
    marginRight: isMobile ? 8 : 12,
    flexShrink: 0,
  };

  const textContainerStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flexGrow: 1,
    minWidth: 0,
    textAlign: "left",
  };

  return (
    <div style={filterItemStyle}>
      <div style={iconStyle}>{icon}</div>
      <div style={textContainerStyle}>
        <FilterLabel label={label} isMobile={isMobile} />
        {children}
      </div>
    </div>
  );
};

// Define the default filter state
const DEFAULT_FILTERS = {
  country: "UNITED ARAB EMIRATES",
  city: "DUBAI",
  propertyType: "ALL",
  priceRange: "1-10M",
};

const HeroSection = ({ filters, onFilterChange, isMobile = false }) => {
  const [localFilters, setLocalFilters] = useState(DEFAULT_FILTERS);
  const screens = useBreakpoint();

  // Sync local filters with parent filters
  useEffect(() => {
    if (JSON.stringify(filters) !== JSON.stringify(localFilters)) {
      setLocalFilters(filters);
    }
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleSearch = () => {
    onFilterChange(localFilters);
  };

  const priceRanges = [
    { label: "1-10M", value: "1-10M" },
    { label: "Under 1M", value: "0-1000000" },
    { label: "1M - 5M", value: "1000000-5000000" },
    { label: "5M - 10M", value: "5000000-10000000" },
    { label: "10M - 20M", value: "10000000-20000000" },
    { label: "20M+", value: "20000000-100000000" },
  ];

  const propertyTypes = [
    "ALL",
    "Villa",
    "Apartment",
    "Building",
    "Plot",
    "Commercial",
    "Office",
  ];

  const selectStyle = {
    width: "100%",
    height: "auto",
    fontSize: isMobile ? 12 : 14,
    fontWeight: 600,
    textAlign: "left",
  };

  const buttonColor = "#E7AC72";

  return (
    <div
      style={{
        padding: isMobile ? "40px 15px" : "80px 0",
        textAlign: "center",
      }}
    >
      {/* --- Main Text Section --- */}
      <Title
        level={isMobile ? 2 : 1}
        style={{
          color: "#fff",
          fontWeight: 700,
          fontFamily: "Inknut Antiqua",
          marginBottom: 10,
          fontSize: isMobile ? "28px" : "48px",
          lineHeight: "1.2",
          padding: isMobile ? "0 5px" : "0",
        }}
      >
        Connecting Buyers and Sellers Direct
      </Title>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          color: "#fff",
          fontSize: isMobile ? "13px" : "16px",
          gap: isMobile ? "8px" : "20px",
          marginBottom: isMobile ? 25 : 40,
          padding: isMobile ? "0 5px" : "0",
        }}
      >
        <span>✓ Trusted Deals</span>
        <span>✓ Direct Connections</span>
        <span>✓ Efficient Process</span>
        <span>✓ Totally Secure</span>
      </div>

      {/* --- Transparent Filter Container --- */}
      <Row justify="center">
        <Col xs={24} sm={22} md={22} lg={20} xl={18}>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.4)",
              padding: isMobile ? "25px 15px" : "40px 30px",
              borderRadius: "15px",
              margin: isMobile ? "0 5px" : "0",
            }}
          >
            {/* --- Filter Bar (White) --- */}
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                gap: isMobile ? "20px" : "20px",
                flexWrap: isMobile ? "wrap" : "nowrap",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "center",
              }}
            >
              {/* Filter Fields Container */}
              <div
                style={{
                  background: "white",
                  borderRadius: "15px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  display: "flex",
                  alignItems: "stretch",
                  height: isMobile ? "auto" : 70,
                  flex: 1,
                  flexDirection: isMobile ? "column" : "row",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                {/* Filter Fields */}
                <div
                  style={{
                    display: "flex",
                    flexGrow: 1,
                    borderRadius: "15px",
                    overflow: "hidden",
                    flexDirection: isMobile ? "column" : "row",
                    width: "100%",
                  }}
                >
                  {/* 1. Country Filter */}
                  <FilterField
                    icon={<UserOutlined />}
                    label="COUNTRY"
                    flexValue={3}
                    isMobile={isMobile}
                  >
                    <Select
                      value={localFilters.country || "UNITED ARAB EMIRATES"}
                      onChange={(value) => handleFilterChange("country", value)}
                      bordered={false}
                      showArrow={true}
                      style={selectStyle}
                      popupMatchSelectWidth={false}
                      placeholder="UNITED ARAB EMIRATES"
                      dropdownStyle={{ textAlign: "left" }}
                      size={isMobile ? "small" : "middle"}
                    >
                      <Option value="UNITED ARAB EMIRATES">
                        UNITED ARAB EMIRATES
                      </Option>
                      <Option value="SAUDI ARABIA">SAUDI ARABIA</Option>
                    </Select>
                  </FilterField>

                  {/* 2. City Filter */}
                  <FilterField
                    icon={<EnvironmentOutlined />}
                    label="CITY"
                    flexValue={2}
                    isMobile={isMobile}
                  >
                    <Select
                      value={localFilters.city || "DUBAI"}
                      onChange={(value) => handleFilterChange("city", value)}
                      bordered={false}
                      showArrow={true}
                      style={selectStyle}
                      popupMatchSelectWidth={false}
                      placeholder="DUBAI"
                      dropdownStyle={{ textAlign: "left" }}
                      size={isMobile ? "small" : "middle"}
                    >
                      <Option value="DUBAI">DUBAI</Option>
                      <Option value="ABU DHABI">ABU DHABI</Option>
                      <Option value="SHARJAH">SHARJAH</Option>
                      <Option value="AJMAN">AJMAN</Option>
                      <Option value="UMM AL QUWAIN">UMM AL QUWAIN</Option>
                      <Option value="RAS AL KHAIMAH">RAS AL KHAIMAH</Option>
                      <Option value="FUJAIRAH">FUJAIRAH</Option>
                    </Select>
                  </FilterField>

                  {/* 3. Property Type Filter */}
                  <FilterField
                    icon={<HomeOutlined />}
                    label="TYPE"
                    flexValue={2}
                    isMobile={isMobile}
                  >
                    <Select
                      value={localFilters.propertyType || "ALL"}
                      onChange={(value) =>
                        handleFilterChange("propertyType", value)
                      }
                      bordered={false}
                      showArrow={true}
                      style={selectStyle}
                      popupMatchSelectWidth={false}
                      dropdownStyle={{ textAlign: "left" }}
                      size={isMobile ? "small" : "middle"}
                    >
                      {propertyTypes.map((type) => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  </FilterField>

                  {/* 4. Price Range Filter */}
                  <FilterField
                    icon={<DollarOutlined />}
                    label="VALUE"
                    isLast={true}
                    flexValue={3}
                    isMobile={isMobile}
                  >
                    <Select
                      value={localFilters.priceRange || "1-10M"}
                      onChange={(value) =>
                        handleFilterChange("priceRange", value)
                      }
                      bordered={false}
                      showArrow={true}
                      style={selectStyle}
                      popupMatchSelectWidth={false}
                      placeholder="1-10M"
                      dropdownStyle={{ textAlign: "left" }}
                      size={isMobile ? "small" : "middle"}
                    >
                      <Option value="">Any Price</Option>
                      {priceRanges.map((range) => (
                        <Option key={range.value} value={range.value}>
                          {range.label}
                        </Option>
                      ))}
                    </Select>
                  </FilterField>
                </div>
              </div>

              {/* Search Button */}
              <Button
                type="primary"
                onClick={handleSearch}
                style={{
                  background: buttonColor,
                  color: "white",
                  fontWeight: "bold",
                  width: isMobile ? "100%" : "auto",
                  minWidth: isMobile ? "100%" : 160,
                  border: "none",
                  borderRadius: "15px",
                  height: isMobile ? 45 : 70,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: isMobile ? "15px" : "18px",
                  padding: isMobile ? "0 20px" : "0 30px",
                  boxShadow: "0 4px 12px rgba(231, 172, 114, 0.4)",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  if (!isMobile) {
                    e.target.style.background = "#d99c60";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 6px 16px rgba(231, 172, 114, 0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isMobile) {
                    e.target.style.background = buttonColor;
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow =
                      "0 4px 12px rgba(231, 172, 114, 0.4)";
                  }
                }}
              >
                SEARCH
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default HeroSection;
